interface OfflineData {
  id: string
  type: 'news' | 'products' | 'solutions' | 'careers' | 'faq' | 'contact'
  data: any
  timestamp: number
  version: number
}

class OfflineStorageService {
  private readonly STORAGE_KEY = 'deeplink_offline_data'
  private readonly VERSION = 1

  // 存储数据到本地
  async storeData(type: OfflineData['type'], id: string, data: any): Promise<void> {
    try {
      const offlineData: OfflineData = {
        id,
        type,
        data,
        timestamp: Date.now(),
        version: this.VERSION
      }

      const existingData = this.getStoredData()
      const updatedData = {
        ...existingData,
        [`${type}_${id}`]: offlineData
      }

      localStorage.setItem(this.STORAGE_KEY, JSON.stringify(updatedData))
    } catch (error) {
      console.error('Failed to store offline data:', error)
    }
  }

  // 获取存储的数据
  getStoredData(): Record<string, OfflineData> {
    try {
      const data = localStorage.getItem(this.STORAGE_KEY)
      return data ? JSON.parse(data) : {}
    } catch (error) {
      console.error('Failed to get stored data:', error)
      return {}
    }
  }

  // 获取特定类型的数据
  getDataByType(type: OfflineData['type']): OfflineData[] {
    const allData = this.getStoredData()
    return Object.values(allData).filter(item => item.type === type)
  }

  // 获取特定ID的数据
  getDataById(type: OfflineData['type'], id: string): OfflineData | null {
    const allData = this.getStoredData()
    const key = `${type}_${id}`
    return allData[key] || null
  }

  // 批量存储数据
  async storeBatchData(type: OfflineData['type'], items: Array<{ id: string; data: any }>): Promise<void> {
    try {
      const existingData = this.getStoredData()
      const updatedData = { ...existingData }

      items.forEach(item => {
        const offlineData: OfflineData = {
          id: item.id,
          type,
          data: item.data,
          timestamp: Date.now(),
          version: this.VERSION
        }
        updatedData[`${type}_${item.id}`] = offlineData
      })

      localStorage.setItem(this.STORAGE_KEY, JSON.stringify(updatedData))
    } catch (error) {
      console.error('Failed to store batch data:', error)
    }
  }

  // 清理过期数据（超过30天）
  cleanExpiredData(): void {
    try {
      const allData = this.getStoredData()
      const thirtyDaysAgo = Date.now() - (30 * 24 * 60 * 60 * 1000)
      
      const filteredData = Object.entries(allData).reduce((acc, [key, value]) => {
        if (value.timestamp > thirtyDaysAgo) {
          acc[key] = value
        }
        return acc
      }, {} as Record<string, OfflineData>)

      localStorage.setItem(this.STORAGE_KEY, JSON.stringify(filteredData))
    } catch (error) {
      console.error('Failed to clean expired data:', error)
    }
  }

  // 获取存储统计信息
  getStorageStats(): {
    totalItems: number
    totalSize: number
    typeCounts: Record<string, number>
    oldestItem: Date | null
    newestItem: Date | null
  } {
    const allData = this.getStoredData()
    const items = Object.values(allData)
    
    const typeCounts = items.reduce((acc, item) => {
      acc[item.type] = (acc[item.type] || 0) + 1
      return acc
    }, {} as Record<string, number>)

    const timestamps = items.map(item => item.timestamp)
    const totalSize = JSON.stringify(allData).length

    return {
      totalItems: items.length,
      totalSize,
      typeCounts,
      oldestItem: timestamps.length > 0 ? new Date(Math.min(...timestamps)) : null,
      newestItem: timestamps.length > 0 ? new Date(Math.max(...timestamps)) : null
    }
  }

  // 清空所有数据
  clearAllData(): void {
    localStorage.removeItem(this.STORAGE_KEY)
  }

  // 清空特定类型的数据
  clearDataByType(type: OfflineData['type']): void {
    const allData = this.getStoredData()
    const filteredData = Object.entries(allData).reduce((acc, [key, value]) => {
      if (value.type !== type) {
        acc[key] = value
      }
      return acc
    }, {} as Record<string, OfflineData>)

    localStorage.setItem(this.STORAGE_KEY, JSON.stringify(filteredData))
  }
}

export const offlineStorageService = new OfflineStorageService()
export default offlineStorageService